import axios from 'axios'
import { getRefreshToken, getToken, setToken } from '@/utils/token'
import { getCache, setCache } from '@/utils/cache'
import { IUser } from '@/modules/Auth/auth-types'
import { applyAppTokenRefreshInterceptor } from '@conexasaude/apply-refresh-token'

axios.defaults.headers.post['Content-Type'] = 'application/json'

const AUTH_HEADER_NAME = 'token'

export interface ConexaResponse<T> {
  status: number
  msg: string | null
  object: T | null
  timestamp: number
}

export type ConexaGenericError = {
  message?: string
  response?: {
    data?: {
      msg?: string
    }

    msg?: string
  }
}

export interface RefreshTokenResponse {
  accessToken: string
}

const httpClient = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  headers: {
    'x-device': 'web',
    crossDomain: true,
  },
})

httpClient.interceptors.request.use(config => {
  const token = getToken()

  if (token) {
    config.headers.common[AUTH_HEADER_NAME] = token
    httpClient.defaults.headers.common[AUTH_HEADER_NAME] = token
  }

  if (config.params) {
    config.params.o = 'web'
  } else {
    config.params = { o: 'web' }
  }

  return config
})

const getUserData = () => {
  const users = getCache<IUser[]>('users')
  const userId = getCache<number>('user')
  const userIndex = users.findIndex(user => user?.usuario?.id === userId)

  return {
    users,
    userId,
    userIndex,
  }
}

const handleRefreshToken = async () => {
  const { users, userIndex } = getUserData()
  const refreshToken = getRefreshToken()

  const res = await httpClient.post<RefreshTokenResponse>('/token/refresh', {
    usuario_id: users[userIndex]?.usuario?.id,
    refreshToken,
    audience: 'WEB',
  })

  return res?.data?.accessToken
}

applyAppTokenRefreshInterceptor({
  client: httpClient,
  options: {
    shouldIntercept(error) {
      const accessUrls = ['/integration/app-paciente/usuario/login/web']

      return (
        !accessUrls.includes(error.config.url) &&
        error?.response &&
        error?.response?.status === 401 &&
        error?.response?.data?.msg?.includes('INVALID_ACCESS_TOKEN')
      )
    },
  },
  handleRefreshToken,
  onSuccess(accessToken) {
    const { users, userIndex } = getUserData()

    const updatingUsers = [...users]
    updatingUsers[userIndex].token = accessToken

    setToken(accessToken)
    setCache('users', updatingUsers)
  },
  onError(error) {
    if (error.code === '429') return

    if (error.response.status === 401) {
      console.log('Failed to refresh auth token')
      sessionStorage.clear()
      window.location.href = '/login'
    }
  },
})

Object.defineProperties(httpClient, {
  setToken: {
    value(token: string) {
      httpClient.defaults.headers.common[AUTH_HEADER_NAME] = `${token}`
    },
  },
  clearToken: {
    value() {
      httpClient.defaults.headers.common[AUTH_HEADER_NAME] = null
    },
  },
  hasToken: {
    value() {
      return httpClient.defaults.headers.common[AUTH_HEADER_NAME]
    },
  },
})

export default httpClient
